import {
  Badge,
  Box,
  Button,
  Checkbox,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerOverlay,
  FormControl,
  FormLabel,
  Image,
  Input,
  NumberDecrementStepper,
  NumberIncrementStepper,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  Select,
  Text,
  Textarea,
  useToast,
} from "@chakra-ui/react";
import ReactSelect from "react-select";

import React, { useCallback, useEffect, useState } from "react";
import { Order, OrderState } from "../../../entity/Order";
import { useSelector } from "react-redux";
import { selectProducts } from "../../../store/selectors/ProductSelector";
import { selectStore } from "../../../store/selectors/StoreSelector";
import { CONSTANTS } from "../../../utils/Constants";
import { useTranslation } from "react-i18next";
import "./AddNewOrder.css";
import {
  selectListDeliveryCompanies,
  selectStoreConfig,
} from "../../../store/selectors/DeliverySelector";
import { getConnectedDeliveryCompanies } from "../../../helpers/GetConnectedDeliveryCompanies";
import { Pack } from "../../../entity/Product";
import { COLORS } from "../../../utils/Colors";

interface AddNewOrderProps {
  isOpen: boolean;
  onClose: () => void;
  onCreate: (order: Order) => Promise<void>;
  onUpdate: (order: Order) => Promise<void>;
  order: Order;
}

// react-select menus render in a portal on <body>, above the Chakra drawer.
const selectPortalStyles = {
  menuPortal: (base: any) => ({ ...base, zIndex: 2000 }),
};

export const AddNewOrder = ({
  isOpen,
  onClose,
  onCreate,
  onUpdate,
  order,
}: AddNewOrderProps) => {
  const { t } = useTranslation();
  const toast = useToast();

  // Selectors
  const store = useSelector(selectStore);
  const deliveryCompanies = useSelector(selectListDeliveryCompanies);
  const storeConfig = useSelector(selectStoreConfig);

  // Hooks
  const products = useSelector(selectProducts);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedProducts, setSelectedProducts] = useState<string[]>([]);
  const [productQuantities, setProductQuantities] = useState<
    Record<string, number>
  >({});
  const [productPrices, setProductPrices] = useState<Record<string, number>>(
    {},
  );
  const [selectedPacks, setSelectedPacks] = useState<
    Record<string, Pack | null>
  >({});

  const [manualTotalAmount, setManualTotalAmount] = useState<number | null>(
    null,
  );
  const [isManualTotal, setIsManualTotal] = useState(false);

  const [formData, setFormData] = useState<Order>({
    userId: "",
    productIds: [],
    address: "",
    name: "",
    phone: "",
    governorate: "",
    totalAmount: 0,
    status: OrderState.PENDING,
    history: [],
    storeId: store?.id!!,
    tentativesNumber: 0,
  });
  const [isConfirmed, setIsConfirmed] = useState(false);

  const [tentativesNumber, setTentativesNumber] = useState(0);

  useEffect(() => {
    if (order?.tentativesNumber !== undefined) {
      setTentativesNumber(order.tentativesNumber);
    }
  }, [order?.tentativesNumber]);

  const [formErrors, setFormErrors] = useState<Record<string, string>>({});

  const connectedDeliveryCompanies = getConnectedDeliveryCompanies(
    deliveryCompanies!!,
    storeConfig!!,
  );

  const calculateTotal = useCallback(() => {
    let isAProductHasDeliveryPrice = order?.cart?.find(
      (c) => c.selectedPack?.deliveryPrice == 0,
    );

    if (isManualTotal && manualTotalAmount !== null) {
      return manualTotalAmount;
    }

    const total = selectedProducts.reduce((sum, productId) => {
      const product = products.find((p) => p.id === productId);
      const quantity = productQuantities[productId] || 1;
      const selectedPack = selectedPacks[productId];

      let unitPrice = productPrices[productId] || product?.price || 0;
      let deliveryPrice = 0;
      if (!isAProductHasDeliveryPrice) {
        deliveryPrice = product?.deliveryPrice ?? 0;
      }

      // If a pack is selected for this product, use pack pricing
      if (selectedPack) {
        unitPrice = selectedPack.discount ?? selectedPack.price;
        if (!isAProductHasDeliveryPrice) {
          deliveryPrice = selectedPack.deliveryPrice ?? 0;
        }
      }

      return sum + quantity * unitPrice + deliveryPrice!;
    }, 0);

    return total;
  }, [
    selectedProducts,
    productQuantities,
    productPrices,
    products,
    selectedPacks,
    manualTotalAmount,
    isManualTotal,
  ]);

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >,
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    // Clear error for this field
    if (formErrors[name]) {
      setFormErrors((prev) => {
        const updated = { ...prev };
        delete updated[name];
        return updated;
      });
    }
  };

  const validateForm = () => {
    const errors: Record<string, string> = {};

    if (!formData.name?.trim())
      errors.name = t("validation.customer_name_required");
    if (!formData.phone?.trim()) errors.phone = t("validation.phone_required");
    if (selectedProducts.length === 0)
      errors.products = t("validation.select_at_least_one_product");

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handlePackChange = (selectedOption: any, productId: string) => {
    const product = products.find((p) => p.id === productId);
    if (!product) return;

    if (selectedOption) {
      const selectedPackData = product.packs?.find(
        (pack) => pack.name === selectedOption.value,
      );

      if (selectedPackData) {
        setSelectedPacks((prev) => ({
          ...prev,
          [productId]: selectedPackData,
        }));

        // Update the price for this product based on the pack
        const packPrice = selectedPackData.discount ?? selectedPackData.price;
        setProductPrices((prev) => ({
          ...prev,
          [productId]: packPrice,
        }));
      }
    } else {
      setSelectedPacks((prev) => ({
        ...prev,
        [productId]: null,
      }));
      setProductPrices((prev) => ({
        ...prev,
        [productId]: product.price,
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      toast({
        title: t("toast.validation_error"),
        description: t("toast.fill_required_fields"),
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setIsLoading(true);

    try {
      const totalAmount = calculateTotal();

      // Build cart with selected packs
      const cart = selectedProducts.map((productId) => {
        const product = products.find((p) => p.id === productId);
        const selectedPack = selectedPacks[productId];

        return {
          productId,
          quantity: productQuantities[productId] || 1,
          price: productPrices[productId] || product?.price || 0,
          selectedPack: selectedPack || undefined,
        };
      });

      if (order && onUpdate) {
        const updatedOrder: Order = {
          id: order.id,
          userId: formData.userId,
          name: formData.name,
          phone: formData.phone,
          governorate: formData.governorate,
          address: formData.address,
          status: formData.status,
          productIds: selectedProducts,
          totalAmount: totalAmount,
          updatedAt: new Date(),
          history: formData.history,
          storeId: formData.storeId,
          notes: formData.notes,
          deliveryCompany: formData.deliveryCompany,
          deliveryReference: order.deliveryReference,
          cart: cart,
          tentativesNumber: tentativesNumber,
        };

        await onUpdate(updatedOrder);
        toast({
          title: t("toast.success"),
          description: t("toast.order_updated_successfully"),
          status: "success",
          duration: 3000,
          isClosable: true,
        });
      } else {
        const orderToCreate: Order = {
          ...formData,
          productIds: selectedProducts,
          totalAmount: totalAmount,
          createdAt: new Date(),
          updatedAt: new Date(),
          deliveryCompany: formData.deliveryCompany,
          cart: cart,
        };

        await onCreate(orderToCreate);
        toast({
          title: t("toast.success"),
          description: t("toast.order_created_successfully"),
          status: "success",
          duration: 3000,
          isClosable: true,
        });
      }
      onClose();
    } catch (error) {
      console.error("Error saving order:", error);
      toast({
        title: t("toast.error"),
        description:
          error instanceof Error ? error.message : t("toast.an_error_occurred"),
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleStatusChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    if (
      e.target.value === OrderState.CONFIRMED ||
      e.target.value === OrderState.EXCHANGED
    ) {
      setIsConfirmed(true);
    } else {
      setIsConfirmed(false);
    }
    setFormData((prev) => ({
      ...prev,
      status: e.target.value as OrderState,
    }));
  };

  const handleProductChange = (selected: any) => {
    const selectedIds = selected ? selected.map((item: any) => item.value) : [];
    setSelectedProducts(selectedIds);

    const newQuantities = { ...productQuantities };
    const newPrices = { ...productPrices };

    selectedIds.forEach((id: string) => {
      if (!productQuantities[id]) {
        newQuantities[id] = 1;
      }
      if (!productPrices[id]) {
        const product = products.find((p) => p.id === id);
        newPrices[id] = product?.price || 0;
      }
    });

    setProductQuantities(newQuantities);
    setProductPrices(newPrices);

    // Clean up packs for removed products
    const newSelectedPacks = { ...selectedPacks };
    Object.keys(newSelectedPacks).forEach((productId) => {
      if (!selectedIds.includes(productId)) {
        delete newSelectedPacks[productId];
      }
    });
    setSelectedPacks(newSelectedPacks);

    // Reset manual total when products change
    if (isManualTotal) {
      setIsManualTotal(false);
      setManualTotalAmount(null);
    }

    setFormData((prev) => ({
      ...prev,
      productIds: selectedIds,
    }));

    if (formErrors.products) {
      setFormErrors((prev) => {
        const updated = { ...prev };
        delete updated.products;
        return updated;
      });
    }
  };

  const statusColors: Record<string, string> = {
    [OrderState.PENDING]: "orange",
    [OrderState.CONFIRMED]: "blue",
    [OrderState.DELIVERED]: "green",
    [OrderState.CANCELED]: "red",
    [OrderState.RETURNED]: "gray",
    [OrderState.ARCHIVED]: "purple",
    [OrderState.UPLOADED]: COLORS.OrderState.UPLOADED,
    [OrderState.EXCHANGED]: COLORS.OrderState.EXCHANGED,
  };

  const handleChangeDeliveryCompany = (value: string) => {
    setFormData((prev) => ({
      ...prev,
      deliveryCompany: value,
    }));
  };

  const handleManualTotalToggle = (checked: boolean) => {
    setIsManualTotal(checked);
    if (!checked) {
      setManualTotalAmount(null);
    }
  };

  const resetManualTotal = () => {
    if (isManualTotal) {
      setIsManualTotal(false);
      setManualTotalAmount(null);
    }
  };

  React.useEffect(() => {
    if (order) {
      setFormData(order);

      const initialProductIds = order.productIds?.length
        ? order.productIds
        : order.cart?.map((item) => item.productId) || [];
      setSelectedProducts(initialProductIds);

      const initialQuantities: Record<string, number> = {};
      const initialPrices: Record<string, number> = {};
      const initialPacks: Record<string, Pack | null> = {};

      order.cart?.forEach((item) => {
        if (item.productId) {
          if (item.quantity) {
            initialQuantities[item.productId] = item.quantity;
          }
          if (item.price) {
            initialPrices[item.productId] = item.price;
          }
          if (item.selectedPack) {
            initialPacks[item.productId] = item.selectedPack;
          }
        }
      });

      setProductQuantities((prev) => ({ ...prev, ...initialQuantities }));
      setProductPrices((prev) => ({ ...prev, ...initialPrices }));
      setSelectedPacks((prev) => ({ ...prev, ...initialPacks }));
    } else {
      setFormData({
        userId: "",
        productIds: [],
        address: "",
        name: "",
        phone: "",
        governorate: "",
        totalAmount: 0,
        status: OrderState.PENDING,
        history: [],
        storeId: store?.id || "",
      });
      setSelectedProducts([]);
      setProductQuantities({});
      setProductPrices({});
      setSelectedPacks({});
    }
    setFormErrors({});
    setIsManualTotal(false);
    setManualTotalAmount(null);
  }, [order, isOpen, store?.id]);

  useEffect(() => {
    if (!isManualTotal) {
      const total = calculateTotal();
      setFormData((prev) => ({
        ...prev,
        totalAmount: total,
      }));
    }
  }, [calculateTotal, isManualTotal]);

  const handleCreateExchangeFromOrder = () => {
    setFormData((prev) => ({
      ...prev,
      status: OrderState.EXCHANGED,
    }));
  };

  const productOptions = products.map((p) => ({
    value: p.id,
    label: p.name,
    product: p,
    imageUrl: p.imageUrl?.[0] || "/placeholder-product.png",
    price: p.price,
    inStock: p.inStock,
  }));

  const displayTotal = isManualTotal
    ? (manualTotalAmount ?? 0)
    : calculateTotal();

  return (
    <Drawer isOpen={isOpen} placement="right" onClose={onClose} size="full">
      <DrawerOverlay className="update-order-overlay" />
      <DrawerContent className="update-order-drawer">
        <DrawerCloseButton className="update-order-close-button" />

        {/* ───────── Header ───────── */}
        <DrawerHeader className="update-order-header">
          <div className="update-order-header-main">
            <Text className="update-order-title">
              {order
                ? t("addNewOrder.edit_order")
                : t("addNewOrder.create_new_order")}
            </Text>
            {order && (
              <div className="update-order-header-meta">
                <Badge
                  className="update-order-status-badge"
                  colorScheme={statusColors[order.status] || "gray"}
                >
                  {t(`orderStatus.${order.status.toLowerCase()}`)}
                </Badge>
                <Text className="update-order-order-id update-order-hidden-in-mobile">
                  {t("addNewOrder.order_id")}: {order.id}
                </Text>
              </div>
            )}
          </div>

          <Button
            className="update-order-exchange-button"
            variant="outline"
            onClick={handleCreateExchangeFromOrder}
          >
            {t("addNewOrder.createExchangeFromOrder")}
          </Button>
        </DrawerHeader>

        <form onSubmit={handleSubmit} className="update-order-form">
          <DrawerBody className="update-order-body">
            <div className="update-order-layout">
              {/* ───────── Main column ───────── */}
              <div className="update-order-main">
                {/* Order details */}
                <section className="update-order-section">
                  <h3 className="update-order-section-title">
                    {t("addNewOrder.order_details")}
                  </h3>
                  <div className="update-order-order-input-container">
                    <FormControl
                      isRequired
                      className="update-order-order-input"
                    >
                      <FormLabel className="update-order-label">
                        {t("addNewOrder.status")}
                      </FormLabel>
                      <Select
                        name="status"
                        value={formData.status}
                        onChange={handleStatusChange}
                        className="update-order-field update-order-status-select"
                      >
                        <option
                          className="update-order-status-option"
                          value={OrderState.PENDING}
                        >
                          {t("orderStatus.pending")}
                        </option>
                        <option
                          className="update-order-status-option"
                          value={OrderState.CONFIRMED}
                        >
                          {t("orderStatus.confirmed")}
                        </option>

                        <option
                          className="update-order-status-option"
                          value={OrderState.TENTATIVE}
                        >
                          {t("orderStatus.tentative")}
                        </option>

                        <option
                          className="update-order-status-option"
                          value={OrderState.EXCHANGED}
                        >
                          {t("orderStatus.exchanged")}
                        </option>
                        <option
                          className="update-order-status-option"
                          value={OrderState.DELIVERED}
                        >
                          {t("orderStatus.delivered")}
                        </option>
                        <option
                          className="update-order-status-option"
                          value={OrderState.CANCELED}
                        >
                          {t("orderStatus.cancelled")}
                        </option>
                        <option
                          className="update-order-status-option"
                          value={OrderState.RETURNED}
                        >
                          {t("orderStatus.returned")}
                        </option>
                        <option
                          className="update-order-status-option"
                          value={OrderState.UPLOADED}
                        >
                          {t("orderStatus.uploaded")}
                        </option>
                      </Select>
                    </FormControl>

                    <FormControl
                      isDisabled={!isConfirmed}
                      className="update-order-order-input"
                    >
                      <FormLabel className="update-order-label">
                        {t("delivery.deliveryCompanies")}
                      </FormLabel>
                      <Select
                        name="deliveryCompany"
                        value={formData.deliveryCompany}
                        onChange={(e) =>
                          handleChangeDeliveryCompany(e.target.value)
                        }
                        className="update-order-field"
                      >
                        <option value=""></option>
                        {connectedDeliveryCompanies.map((company) => (
                          <option key={company.id} value={company.id}>
                            {company.name}
                          </option>
                        ))}
                      </Select>
                    </FormControl>

                    {formData.status === OrderState.TENTATIVE && (
                      <FormControl>
                        <FormLabel className="update-order-label">
                          {t("addNewOrder.tentativesNumber")}
                        </FormLabel>
                        <div className="order-modify-tentatives">
                          <button
                            type="button"
                            onClick={() => {
                              if ((tentativesNumber || 0) > 0) {
                                setTentativesNumber(
                                  (tentativesNumber || 0) - 1,
                                );
                              }
                            }}
                          >
                            -
                          </button>
                          <Input
                            name="tentativesNumber"
                            value={tentativesNumber}
                            className="update-order-field"
                            readOnly
                          />
                          <button
                            type="button"
                            onClick={() => {
                              setTentativesNumber((tentativesNumber || 0) + 1);
                            }}
                          >
                            +
                          </button>
                        </div>
                      </FormControl>
                    )}
                  </div>
                </section>

                {/* Customer information */}
                <section className="update-order-section">
                  <h3 className="update-order-section-title">
                    {t("addNewOrder.customer_information")}
                  </h3>
                  <div className="update-order-order-input-container">
                    <FormControl
                      isRequired
                      isInvalid={!!formErrors.name}
                      className="update-order-order-input"
                    >
                      <FormLabel className="update-order-label">
                        {t("addNewOrder.customer_name")}
                      </FormLabel>
                      <Input
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        placeholder={t("addNewOrder.customer_name_placeholder")}
                        className="update-order-field"
                      />
                      {formErrors.name && (
                        <Text className="update-order-error-text">
                          {formErrors.name}
                        </Text>
                      )}
                    </FormControl>

                    <FormControl
                      isRequired
                      isInvalid={!!formErrors.phone}
                      className="update-order-order-input"
                    >
                      <FormLabel className="update-order-label">
                        {t("addNewOrder.phone_number")}
                      </FormLabel>
                      <Input
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        placeholder={t("addNewOrder.phone_placeholder")}
                        className="update-order-field"
                      />
                      {formErrors.phone && (
                        <Text className="update-order-error-text">
                          {formErrors.phone}
                        </Text>
                      )}
                    </FormControl>

                    <FormControl
                      isRequired
                      className="update-order-order-input"
                    >
                      <FormLabel className="update-order-label">
                        {t("addNewOrder.governorate")}
                      </FormLabel>
                      <Select
                        name="governorate"
                        value={formData.governorate}
                        onChange={(e) => {
                          setFormData((prev) => ({
                            ...prev,
                            governorate: e.target.value,
                          }));
                        }}
                        className="update-order-field"
                      >
                        {CONSTANTS.GOVERNORATES.map((governorate) => (
                          <option key={governorate} value={governorate}>
                            {governorate}
                          </option>
                        ))}
                      </Select>
                    </FormControl>

                    <FormControl
                      isRequired
                      className="update-order-order-input"
                    >
                      <FormLabel className="update-order-label">
                        {t("addNewOrder.delivery_address")}
                      </FormLabel>
                      <Textarea
                        name="address"
                        value={formData.address}
                        onChange={handleChange}
                        placeholder={t("addNewOrder.address_placeholder")}
                        className="update-order-field update-order-textarea-compact"
                      />
                    </FormControl>
                  </div>
                </section>

                {/* Products */}
                <section className="update-order-section update-order-order-items-container">
                  <h3 className="update-order-section-title">
                    {t("addNewOrder.order_items")}
                  </h3>
                  <FormControl
                    isInvalid={!!formErrors.products}
                    className="update-order-form-control-mobile"
                  >
                    <FormLabel className="update-order-label">
                      {t("addNewOrder.select_products")}
                    </FormLabel>
                    <ReactSelect
                      className="update-order-main-select-dropdown"
                      classNamePrefix="update-order-react-select"
                      isMulti
                      menuPortalTarget={document.body}
                      styles={selectPortalStyles}
                      options={productOptions}
                      value={productOptions.filter((o) =>
                        selectedProducts.includes(o.value),
                      )}
                      onChange={handleProductChange}
                      formatOptionLabel={(option: any, meta: any) =>
                        meta.context === "menu" ? (
                          <div className="update-order-option-row">
                            <img
                              className="update-order-option-thumb"
                              src={option.imageUrl}
                              alt=""
                            />
                            <div className="update-order-option-meta">
                              <span className="update-order-option-name">
                                {option.label}
                              </span>
                              <span className="update-order-option-sub">
                                {Number(option.price ?? 0).toFixed(2)} TND
                              </span>
                            </div>
                            {option.inStock === false && (
                              <span className="update-order-option-stock">
                                {t("addNewOrder.out_of_stock", "Out of stock")}
                              </span>
                            )}
                          </div>
                        ) : (
                          option.label
                        )
                      }
                    />
                    {formErrors.products && (
                      <Text className="update-order-error-text">
                        {formErrors.products}
                      </Text>
                    )}
                  </FormControl>

                  {selectedProducts.length === 0 ? (
                    <div className="update-order-empty-items">
                      {t(
                        "addNewOrder.empty_items",
                        "Search above to add products to this order.",
                      )}
                    </div>
                  ) : (
                    <div className="update-order-selected-products-box">
                      <div className="update-order-selected-items-header">
                        <div>
                          <Text className="update-order-selected-items-title">
                            {t("addNewOrder.selected_items")}
                          </Text>
                          <Text className="update-order-selected-items-count">
                            {selectedProducts.length} product
                            {selectedProducts.length !== 1 ? "s" : ""}
                          </Text>
                        </div>
                        <Text className="update-order-total-price">
                          {displayTotal.toFixed(2)} TND
                        </Text>
                      </div>

                      <div className="update-order-product-list">
                        {selectedProducts.map((productId) => {
                          const product = products.find(
                            (p) => p.id === productId,
                          );
                          if (!product) return null;

                          const quantity = productQuantities[productId] || 1;
                          const selectedPack = selectedPacks[productId];
                          const unitPrice =
                            productPrices[productId] || product.price;
                          const deliveryPrice =
                            selectedPack?.deliveryPrice ??
                            product.deliveryPrice ??
                            0;
                          const subtotal = quantity * unitPrice + deliveryPrice;

                          return (
                            <Box
                              key={productId}
                              className="update-order-product-item-card"
                            >
                              <Image
                                className="update-order-product-image"
                                src={
                                  product.imageUrl?.[0] ||
                                  "/placeholder-product.png"
                                }
                                alt={product.name}
                                fallbackSrc="/placeholder-product.png"
                              />

                              <div className="update-order-product-info">
                                <Text
                                  className="update-order-product-name"
                                  noOfLines={1}
                                >
                                  {product.name}
                                </Text>
                                <Text
                                  className="update-order-product-ref"
                                  noOfLines={1}
                                >
                                  {product.ref}
                                </Text>
                              </div>

                              <Button
                                size="sm"
                                variant="ghost"
                                colorScheme="red"
                                aria-label={t("buttons.remove", "Remove")}
                                className="update-order-remove-product-btn"
                                onClick={() => {
                                  const newSelectedProducts =
                                    selectedProducts.filter(
                                      (id) => id !== productId,
                                    );
                                  setSelectedProducts(newSelectedProducts);

                                  const newQuantities = {
                                    ...productQuantities,
                                  };
                                  const newPrices = { ...productPrices };
                                  const newPacks = { ...selectedPacks };
                                  delete newQuantities[productId];
                                  delete newPrices[productId];
                                  delete newPacks[productId];
                                  setProductQuantities(newQuantities);
                                  setProductPrices(newPrices);
                                  setSelectedPacks(newPacks);

                                  resetManualTotal();

                                  setFormData((prev) => ({
                                    ...prev,
                                    productIds: newSelectedProducts,
                                  }));
                                }}
                              >
                                ×
                              </Button>

                              <div className="update-order-product-controls">
                                {/* Quantity */}
                                <div className="update-order-control">
                                  <span className="update-order-mini-label">
                                    {t("buttons.txt_quantity")}
                                  </span>
                                  <NumberInput
                                    className="update-order-product-section-input"
                                    size="sm"
                                    min={1}
                                    max={product.quantity || 999}
                                    value={quantity}
                                    onChange={(value) => {
                                      const newQuantity = parseInt(value) || 1;
                                      setProductQuantities((prev) => ({
                                        ...prev,
                                        [productId]: newQuantity,
                                      }));
                                      resetManualTotal();
                                    }}
                                  >
                                    <NumberInputField className="update-order-number-field" />
                                    <NumberInputStepper>
                                      <NumberIncrementStepper />
                                      <NumberDecrementStepper />
                                    </NumberInputStepper>
                                  </NumberInput>
                                </div>

                                {/* Pack */}
                                {product.packs && product.packs.length > 0 && (
                                  <div className="update-order-control">
                                    <span className="update-order-mini-label">
                                      {t("addNewOrder.pack")}
                                    </span>
                                    <ReactSelect
                                      className="update-order-pack-select"
                                      classNamePrefix="update-order-react-select"
                                      menuPortalTarget={document.body}
                                      styles={selectPortalStyles}
                                      value={
                                        selectedPack
                                          ? {
                                              value: selectedPack.name,
                                              label: selectedPack.name,
                                            }
                                          : null
                                      }
                                      options={product.packs.map((pack) => ({
                                        value: pack.name,
                                        label: `${pack.name}`,
                                        pack,
                                      }))}
                                      onChange={(selectedOption) => {
                                        handlePackChange(
                                          selectedOption,
                                          productId,
                                        );
                                        resetManualTotal();
                                      }}
                                      isClearable
                                      placeholder={t(
                                        "addNewOrder.select_pack",
                                        "Select a pack",
                                      )}
                                    />
                                  </div>
                                )}

                                {/* Unit price */}
                                <div className="update-order-control">
                                  <span className="update-order-mini-label">
                                    {t("addNewOrder.unit_price", "Unit price")}
                                  </span>
                                  <NumberInput
                                    className="update-order-product-section-input"
                                    size="sm"
                                    min={0}
                                    step={0.01}
                                    precision={2}
                                    value={unitPrice}
                                    onChange={(value) => {
                                      const newPrice = parseFloat(value) || 0;
                                      setProductPrices((prev) => ({
                                        ...prev,
                                        [productId]: newPrice,
                                      }));
                                      resetManualTotal();
                                    }}
                                  >
                                    <NumberInputField className="update-order-number-field" />
                                  </NumberInput>
                                </div>
                              </div>

                              <div className="update-order-product-totals">
                                {deliveryPrice > 0 && (
                                  <div className="update-order-price-display">
                                    <span className="update-order-price-label">
                                      {t("addNewOrder.delivery", "Delivery")}
                                    </span>
                                    <span className="update-order-price-value">
                                      {deliveryPrice.toFixed(2)} TND
                                    </span>
                                  </div>
                                )}
                                <div className="update-order-price-display update-order-price-display-strong">
                                  <span className="update-order-price-label">
                                    {t("addNewOrder.subtotal", "Subtotal")}
                                  </span>
                                  <span className="update-order-price-value">
                                    {subtotal.toFixed(2)} TND
                                  </span>
                                </div>
                              </div>
                            </Box>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </section>

                {/* Notes */}
                <section className="update-order-section">
                  <h3 className="update-order-section-title">
                    {t("addNewOrder.additional_notes")}
                  </h3>
                  <FormControl className="update-order-order-input">
                    <Textarea
                      name="notes"
                      aria-label={t("addNewOrder.additional_notes")}
                      value={formData.notes || ""}
                      onChange={handleChange}
                      placeholder={t("addNewOrder.notes_placeholder")}
                      className="update-order-field update-order-textarea-notes"
                    />
                  </FormControl>
                </section>
              </div>

              {/* ───────── Summary column ───────── */}
              <aside className="update-order-summary">
                <section className="update-order-section update-order-summary-card">
                  <h3 className="update-order-section-title">
                    {t("addNewOrder.order_summary", "Order summary")}
                  </h3>

                  <div className="update-order-summary-row">
                    <span>{t("addNewOrder.products", "Products")}</span>
                    <strong>{selectedProducts.length}</strong>
                  </div>

                  <FormControl className="update-order-order-input">
                    <FormLabel className="update-order-label">
                      {t("addNewOrder.total_amount")}
                    </FormLabel>
                    <NumberInput
                      className="update-order-total-amount"
                      value={displayTotal}
                      onChange={(valueAsString, valueAsNumber) => {
                        if (isManualTotal) {
                          if (valueAsNumber && !isNaN(valueAsNumber)) {
                            setManualTotalAmount(valueAsNumber);
                          } else {
                            setManualTotalAmount(0);
                          }
                        }
                      }}
                      precision={2}
                      isDisabled={!isManualTotal}
                    >
                      <NumberInputField className="update-order-total-field" />
                      {isManualTotal && (
                        <NumberInputStepper>
                          <NumberIncrementStepper />
                          <NumberDecrementStepper />
                        </NumberInputStepper>
                      )}
                    </NumberInput>

                    <Checkbox
                      className="update-order-manual-total-toggle"
                      isChecked={isManualTotal}
                      onChange={(e) =>
                        handleManualTotalToggle(e.target.checked)
                      }
                      colorScheme="purple"
                      size="md"
                    >
                      {t("addNewOrder.custom_total", "Set custom total amount")}
                    </Checkbox>

                    <Text className="update-order-helper-text">
                      {isManualTotal
                        ? t(
                            "addNewOrder.custom_total_hint",
                            "Custom total amount set manually",
                          )
                        : t(
                            "addNewOrder.auto_total_hint",
                            "Total calculated from selected products",
                          )}
                    </Text>
                  </FormControl>
                </section>
              </aside>
            </div>
          </DrawerBody>

          {/* ───────── Sticky footer ───────── */}
          <DrawerFooter className="update-order-footer">
            <div className="update-order-footer-total">
              <span className="update-order-footer-total-label">
                {t("addNewOrder.total_amount")}
              </span>
              <strong className="update-order-footer-total-value">
                {displayTotal.toFixed(2)} TND
              </strong>
            </div>

            <div className="update-order-edit-order-actions">
              <Button
                variant="outline"
                onClick={onClose}
                className="update-order-cancel-button"
              >
                {t("addNewOrder.cancel")}
              </Button>
              {/* type="submit" already triggers the form's onSubmit,
                  so no extra onClick (it caused handleSubmit to run twice). */}
              <Button
                type="submit"
                isLoading={isLoading}
                className="update-order-submit-button"
              >
                {order
                  ? t("addNewOrder.update_order")
                  : t("addNewOrder.create_order")}
              </Button>
            </div>
          </DrawerFooter>
        </form>
      </DrawerContent>
    </Drawer>
  );
};
